import axios from "axios";

/**
 * HTTP clients for the OMJ VISION backend.
 *
 * Every request carries the current Clerk session token as a bearer token. The
 * backend verifies that token, resolves it to a MongoDB user, and decides what
 * the caller may do. Nothing here decides anything: a role held in React is a
 * display hint, never a permission.
 */

/**
 * Normalises a base URL coming from the build environment.
 *
 * Create React App **inlines** every `REACT_APP_*` reference at build time. A
 * variable that is declared but left blank — which is exactly what copying
 * `.env.example` or adding an empty Vercel variable produces — is inlined as
 * `""`, not as `undefined`. Code that tests these values with `!== undefined`
 * therefore accepts the empty string as a deliberate setting and skips its own
 * fallback, which is how telemetry requests ended up aimed at the site root
 * instead of at the backend proxy.
 *
 * Blank means "not configured" here, and a trailing slash is dropped so that
 * joining a path never produces a double slash.
 *
 * @param {string|undefined} value
 * @returns {string} the normalised origin, or "" when nothing is configured
 */
export function normalizeBaseUrl(value) {
  const trimmed = (value || "").trim();
  return trimmed ? trimmed.replace(/\/+$/, "") : "";
}

/**
 * Base URL of the Express backend.
 *
 * Empty in local development, where CRA's dev-server proxy forwards to it.
 * In production this must be the **Express** backend's origin. It is not the
 * FastAPI telemetry host: FastAPI serves `/status` and friends and knows
 * nothing about `/api/me` or `/api/admin/*`.
 */
export const API_BASE_URL = normalizeBaseUrl(process.env.REACT_APP_API_URL);

/**
 * Base URL for GoldBot telemetry.
 *
 * Derived from `API_BASE_URL` so that pointing the app at a backend is a
 * single setting. It resolves to the Express backend's authenticated proxy
 * (`/api/goldbot`), never to FastAPI directly — that path has no Clerk check.
 *
 * `REACT_APP_GOLDBOT_API_URL` stays as an override for the case where
 * telemetry is proxied from a different origin than the rest of the API, but
 * it no longer has to be set for the default to be correct.
 */
export const GOLDBOT_BASE_URL =
  normalizeBaseUrl(process.env.REACT_APP_GOLDBOT_API_URL) ||
  `${API_BASE_URL}/api/goldbot`;

/**
 * Supplies the current Clerk session token.
 *
 * Registered by `<ApiAuthBridge />`, which lives inside `<ClerkProvider>` and
 * so has access to Clerk's `getToken`. Kept in a module variable because axios
 * interceptors run outside React's tree.
 */
let getSessionToken = async () => null;

/**
 * @param {() => Promise<string|null>} provider
 */
export function setSessionTokenProvider(provider) {
  getSessionToken = provider || (async () => null);
}

/** Clears the provider on sign-out so no stale token is attached. */
export function clearSessionTokenProvider() {
  getSessionToken = async () => null;
}

/**
 * Builds an axios instance that attaches the Clerk session token.
 *
 * @param {{baseURL?: string, timeout?: number}} [options]
 * @returns {import('axios').AxiosInstance}
 */
export function createAuthorizedClient(options = {}) {
  const client = axios.create({
    baseURL: options.baseURL !== undefined ? options.baseURL : API_BASE_URL,
    timeout: options.timeout || 10000,
  });

  client.interceptors.request.use(async (config) => {
    let token = null;

    try {
      token = await getSessionToken();
    } catch {
      // A token that cannot be minted (signed out, Clerk still loading) simply
      // means an unauthenticated request; the backend answers 401 and the UI
      // reacts to that rather than to an exception here.
      token = null;
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  });

  return client;
}

/** The client used for the platform's own API (`/api/...`). */
export const apiClient = createAuthorizedClient();

/** Error codes the backend's authorization layer returns. */
export const ApiErrorCode = Object.freeze({
  AUTHENTICATION_REQUIRED: "authentication_required",
  NOT_PROVISIONED: "not_provisioned",
  ACCOUNT_DISABLED: "account_disabled",
  FORBIDDEN: "forbidden",
});

/**
 * Reads the backend's error code out of an axios error.
 *
 * @param {unknown} error
 * @returns {string|null}
 */
export function readApiErrorCode(error) {
  return (error && error.response && error.response.data && error.response.data.error && error.response.data.error.code) || null;
}

/**
 * Reads the backend's human-readable message, falling back to a generic one.
 *
 * @param {unknown} error
 * @param {string} fallback
 * @returns {string}
 */
export function readApiErrorMessage(error, fallback = "Something went wrong.") {
  const message =
    error && error.response && error.response.data && error.response.data.error && error.response.data.error.message;
  return message || fallback;
}

/**
 * `GET /api/me` — the signed-in user's application profile.
 *
 * @param {{signal?: AbortSignal}} [options]
 * @returns {Promise<{id: string, email: string, first_name: string, last_name: string, role: string, status: string, permissions: string[]}>}
 */
export async function fetchCurrentUser({ signal } = {}) {
  const response = await apiClient.get("/api/me", { signal });
  return response.data;
}

// --- admin ------------------------------------------------------------------

export async function fetchUsers(params = {}, { signal } = {}) {
  const response = await apiClient.get("/api/admin/users", { params, signal });
  return response.data;
}

/** `GET /api/admin/invitations` — invitation requests not yet accepted. */
export async function fetchInvitations({ signal } = {}) {
  const response = await apiClient.get("/api/admin/invitations", { signal });
  return response.data;
}

export async function inviteUser(payload) {
  const response = await apiClient.post("/api/admin/users/invite", payload);
  return response.data;
}

export async function updateUserRole(clerkUserId, role) {
  const response = await apiClient.patch(
    `/api/admin/users/${encodeURIComponent(clerkUserId)}/role`,
    { role }
  );
  return response.data;
}

export async function updateUserStatus(clerkUserId, status) {
  const response = await apiClient.patch(
    `/api/admin/users/${encodeURIComponent(clerkUserId)}/status`,
    { status }
  );
  return response.data;
}

/**
 * The permission catalog and the role defaults.
 *
 * Fetched rather than duplicated in the browser: the backend is the one
 * definition of what permissions exist, so the editor cannot offer a toggle
 * for something the server does not enforce.
 */
export async function fetchPermissionCatalog({ signal } = {}) {
  const response = await apiClient.get("/api/admin/permissions/catalog", { signal });
  return response.data;
}

/**
 * Replaces one user's permission overrides.
 *
 * The whole map is sent, not a patch: the editor's state is the complete
 * statement of the exceptions that apply, so a cleared toggle is actually
 * cleared rather than left behind where nobody can see it.
 */
export async function updateUserPermissions(clerkUserId, permissions) {
  const response = await apiClient.put(
    `/api/admin/users/${encodeURIComponent(clerkUserId)}/permissions`,
    { permissions }
  );
  return response.data;
}

export async function fetchSubscribers(params = {}, { signal } = {}) {
  const response = await apiClient.get("/api/admin/subscribers", { params, signal });
  return response.data;
}

export async function fetchSubscriberReferrers({ activeOnly = false, signal } = {}) {
  const response = await apiClient.get("/api/admin/referrers", {
    params: activeOnly ? { active_only: true } : {},
    signal,
  });
  return response.data;
}

export async function createReferrer(payload) {
  const response = await apiClient.post("/api/admin/referrers", payload);
  return response.data;
}

export async function renameReferrer(id, payload) {
  const response = await apiClient.put(`/api/admin/referrers/${encodeURIComponent(id)}`, payload);
  return response.data;
}

export async function updateReferrerStatus(id, active) {
  const response = await apiClient.patch(
    `/api/admin/referrers/${encodeURIComponent(id)}/status`,
    { active }
  );
  return response.data;
}

/**
 * The financial position across every subscriber.
 *
 * Deliberately takes no filter argument. The list endpoint above is what the
 * table's search and status controls narrow; this is the overall total, and
 * the page labels it that way so the two are never confused.
 */
export async function fetchSubscriberSummary({ signal } = {}) {
  const response = await apiClient.get('/api/admin/subscribers/summary', { signal });
  return response.data;
}

export async function createSubscriber(payload) {
  const response = await apiClient.post("/api/admin/subscribers", payload);
  return response.data;
}

export async function updateSubscriber(id, payload) {
  const response = await apiClient.put(`/api/admin/subscribers/${encodeURIComponent(id)}`, payload);
  return response.data;
}

export async function renewSubscriber(id, months) {
  const response = await apiClient.post(
    `/api/admin/subscribers/${encodeURIComponent(id)}/renew`,
    { months }
  );
  return response.data;
}

export async function deleteSubscriber(id) {
  await apiClient.delete(`/api/admin/subscribers/${encodeURIComponent(id)}`);
}

// --- free trials ------------------------------------------------------------
//
// Manually managed trial prospects. A separate collection from both users and
// subscribers, and administrator-only on the server.

export async function fetchFreeTrials(params = {}, { signal } = {}) {
  const response = await apiClient.get("/api/admin/free-trials", { params, signal });
  return response.data;
}

export async function createFreeTrial(payload) {
  const response = await apiClient.post("/api/admin/free-trials", payload);
  return response.data;
}

export async function updateFreeTrial(id, payload) {
  const response = await apiClient.patch(
    `/api/admin/free-trials/${encodeURIComponent(id)}`,
    payload
  );
  return response.data;
}

export async function deleteFreeTrial(id) {
  await apiClient.delete(`/api/admin/free-trials/${encodeURIComponent(id)}`);
}

/**
 * Creates the subscriber and marks the trial converted, in one server call.
 *
 * `payload` is an ordinary subscriber payload — the same shape
 * `createSubscriber` takes — because the backend hands it to the existing
 * subscriber service. The trial is only marked converted once that succeeds.
 */
export async function convertFreeTrial(id, payload) {
  const response = await apiClient.post(
    `/api/admin/free-trials/${encodeURIComponent(id)}/convert`,
    payload
  );
  return response.data;
}
